/**
 * EstimateSplitCard Component
 * Displays a single supplier's delivery options in the modal
 * Handles both regular suppliers and remaining items (undefined supplierId)
 */

import React, {
  useCallback,
  useMemo,
  useState,
  useRef,
  useEffect,
} from "react";
import {
  Box,
  Text,
  VStack,
  HStack,
  Flex,
  Icon,
  Radio,
  RadioGroup,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Badge,
  Tag,
  Select,
  Button,
} from "@chakra-ui/react";
import { MdLocationOn } from "react-icons/md";
import { EstimateSplitCardProps, ManualDeliveryOptionEntry } from "./types";
import { getVehicleTypeDisplay } from "./utils/vehicleTypeUtils";
import { useManualOptionHandlers } from "./hooks/useManualOptionHandlers";
import { formatCPTRange } from "./utils/deliveryTimeUtils";
import { calculateDiscountedPrice } from "./utils/deliveryCostUtils";
import { calculateSupplierFulfillingItemsMRP } from "./utils/deliveryItemUtils";
import { useDeliverySelectionValidation } from "./validation/useDeliverySelectionValidation";
import { ValidationError } from "./validation/deliverySelectionValidation";
import { formatDeliveryModeLabel } from "../../utils/DeliveryMode";
import {
  DeliveryModes,
  CPTSlabForManualOption,
  CPTSlabManualDisplay,
} from "../../views/Orders/enums";
import services from "../../views/Orders/services";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

const MANUAL_CPT_SLAB_OPTIONS = [
  {
    value: CPTSlabForManualOption.DIRECT_DELIVERY,
    label: CPTSlabManualDisplay[CPTSlabForManualOption.DIRECT_DELIVERY],
    icon: "🏎️",
  },
  {
    value: CPTSlabForManualOption.AGGREGATED_DELIVERY,
    label: CPTSlabManualDisplay[CPTSlabForManualOption.AGGREGATED_DELIVERY],
    icon: "🚚",
  },
  {
    value: CPTSlabForManualOption.SELF_PICKUP,
    label: CPTSlabManualDisplay[CPTSlabForManualOption.SELF_PICKUP],
    icon: "🚶",
  },
];

const CPTSlabSelector: React.FC<{
  value?: CPTSlabForManualOption;
  onChange: (val: CPTSlabForManualOption) => void;
  isDisabled?: boolean;
}> = ({ value, onChange, isDisabled }) => {
  const measureRef = useRef<HTMLSpanElement>(null);
  const [selectWidth, setSelectWidth] = useState<number>(130);

  const displayText = useMemo(() => {
    if (!value) return "Select CPT Slab";
    const opt = MANUAL_CPT_SLAB_OPTIONS.find((o) => o.value === value);
    return opt ? `${opt.icon} ${opt.label}` : "Select CPT Slab";
  }, [value]);

  useEffect(() => {
    if (measureRef.current) {
      const textWidth = measureRef.current.offsetWidth;
      setSelectWidth(textWidth + 30);
    }
  }, [displayText]);

  return (
    <HStack spacing={2} align="center">
      <Text
        fontSize="12px"
        color="gray.500"
        fontWeight="700"
        whiteSpace="nowrap"
        mb={-1}
      >
        CPT Slab:
      </Text>
      {/* Hidden span to measure text width */}
      <Box
        as="span"
        ref={measureRef}
        position="absolute"
        visibility="hidden"
        whiteSpace="nowrap"
        fontSize="12px"
        fontWeight="600"
      >
        {displayText}
      </Box>
      <Select
        size="xs"
        variant="outline"
        bg="white"
        fontSize="12px"
        fontWeight="600"
        height="24px"
        borderRadius="md"
        value={value || ""}
        onChange={(e) => {
          onChange(Number(e.target.value) as CPTSlabForManualOption);
          e.target.blur();
        }}
        isDisabled={isDisabled}
        placeholder="Select CPT Slab"
        _hover={{ borderColor: "gray.300" }}
        _focus={{
          borderColor: "blue.400",
          boxShadow: "0 0 0 1px var(--chakra-colors-blue-400)",
        }}
        sx={{
          paddingLeft: "4px",
          paddingRight: "22px",
          paddingInlineEnd: "22px !important",
          width: `${selectWidth}px`,
        }}
      >
        {MANUAL_CPT_SLAB_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.icon} {opt.label}
          </option>
        ))}
      </Select>
    </HStack>
  );
};

interface ActionPillButtonProps {
  label: string;
  isSelected: boolean;
  onClick: () => void;
  isDisabled?: boolean;
  colorScheme?: "gray" | "orange";
}

const ActionPillButton: React.FC<ActionPillButtonProps> = ({
  label,
  isSelected,
  onClick,
  isDisabled = false,
  colorScheme = "gray",
}) => {
  const colors = {
    gray: {
      border: "yellow.300",
      text: "yellow.600",
      selectedBg: "yellow.100",
      selectedBorder: "yellow.500",
      selectedText: "yellow.700",
      hoverBorder: "yellow.300",
      hoverBg: "yellow.100",
    },
    orange: {
      border: "orange.300",
      text: "orange.500",
      selectedBg: "orange.100",
      selectedBorder: "orange.500",
      selectedText: "orange.700",
      hoverBorder: "orange.300",
      hoverBg: "orange.100",
    },
  };

  const scheme = colors[colorScheme];

  return (
    <Box
      as="button"
      w="70px"
      h="32px"
      px={1}
      py={1}
      borderRadius="full"
      border="1px solid"
      borderColor={isSelected ? scheme.selectedBorder : scheme.border}
      bg={isSelected ? scheme.selectedBg : "white"}
      cursor={isDisabled ? "not-allowed" : "pointer"}
      opacity={isDisabled ? 0.5 : 1}
      transition="all 0.3s ease-in-out"
      onClick={onClick}
      disabled={isDisabled}
      _hover={{
        borderColor: isDisabled ? "gray.400" : scheme.hoverBorder,
        bg: isDisabled ? "white" : scheme.hoverBg,
      }}
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <Text
        fontSize="0.8rem"
        fontWeight={isSelected ? "700" : "600"}
        color={isSelected ? scheme.selectedText : scheme.text}
        fontFamily="'Nunito','Inter', sans-serif"
      >
        {label}
      </Text>
    </Box>
  );
};

const EstimateSplitCard: React.FC<EstimateSplitCardProps> = ({
  estimateNo,
  supplier,
  selection,
  onSelectionChange,
  isExpired,
  quickEtasMins,
  directDeliveryMinimumDeliveryCost,
}) => {
  const [isSavingManualOption, setIsSavingManualOption] = useState(false);
  const [manualSaveError, setManualSaveError] = useState<string | null>(null);

  const [buyTimeValue, setBuyTimeValue] = useState<string>(() => {
    if (supplier.buyTime) {
      const minutes = parseInt(String(supplier.buyTime));
      if (!isNaN(minutes) && minutes > 0) {
        const hours = minutes / 60;
        // Round to 1 decimal place
        const roundedHours = Math.round(hours * 10) / 10;
        return String(roundedHours);
      }
    }
    return "";
  });

  const isRemainingItems =
    supplier.supplierId === null || supplier.supplierId === undefined;

  const supplierFulfillingItemsMRP = useMemo(() => {
    return calculateSupplierFulfillingItemsMRP(supplier);
  }, [supplier]);

  const { errorsByField } = useDeliverySelectionValidation(selection, {
    directDeliveryMinimumDeliveryCost,
  });

  const handleManualOptionUpdate = useCallback(
    (entry: ManualDeliveryOptionEntry) => {
      onSelectionChange({
        supplierId: supplier.supplierId,
        customerAction: "confirm",
        savedManualDeliveryOptionId: selection?.savedManualDeliveryOptionId,
        savedManualDeliveryOption: selection?.savedManualDeliveryOption,
        deliveryDetails: {
          selectionType: "manual",
          manualEntry: entry,
        },
      });
    },
    [
      supplier.supplierId,
      onSelectionChange,
      selection?.savedManualDeliveryOptionId,
      selection?.savedManualDeliveryOption,
    ],
  );

  const {
    quickEtaOptions,
    handleManualCPTChange,
    handleManualChargeChange,
    handleQuickEtaSelect,
    handleManualCPTSlabChange,
  } = useManualOptionHandlers({
    isExpired,
    quickEtasMins,
    currentManualEntry: selection?.deliveryDetails?.manualEntry,
    onManualEntryChange: handleManualOptionUpdate,
  });

  // Handle option select by index
  const handleOptionSelect = useCallback(
    (index: number) => {
      if (!supplier.deliveryOptions || index >= supplier.deliveryOptions.length)
        return;

      onSelectionChange({
        supplierId: supplier.supplierId,
        customerAction: "confirm",
        savedManualDeliveryOptionId: selection?.savedManualDeliveryOptionId,
        savedManualDeliveryOption: selection?.savedManualDeliveryOption,
        deliveryDetails: {
          selectionType: "option",
          selectedOptionIndex: index,
        },
      });
    },
    [
      supplier.supplierId,
      supplier.deliveryOptions,
      onSelectionChange,
      selection?.savedManualDeliveryOptionId,
      selection?.savedManualDeliveryOption,
    ],
  );

  const selectedOptionIndex =
    selection?.customerAction === "confirm" &&
    selection?.deliveryDetails?.selectionType === "option"
      ? selection.deliveryDetails.selectedOptionIndex
      : undefined;

  const isManualSelected =
    selection?.customerAction === "confirm" &&
    selection?.deliveryDetails?.selectionType === "manual";

  const hasSavedManualDeliveryOption = Boolean(
    selection?.savedManualDeliveryOptionId,
  );

  const savedManualOption = selection?.savedManualDeliveryOption;

  const handleManualOptionSelect = useCallback(() => {
    if (isManualSelected) return;
    // Manual delivery option can be added only once per split.
    if (hasSavedManualDeliveryOption) {
      onSelectionChange({
        supplierId: supplier.supplierId,
        customerAction: "confirm",
        savedManualDeliveryOptionId: selection?.savedManualDeliveryOptionId,
        savedManualDeliveryOption: selection?.savedManualDeliveryOption,
        deliveryDetails: {
          selectionType: "manual",
        },
      });
      return;
    }

    onSelectionChange({
      supplierId: supplier.supplierId,
      customerAction: "confirm",
      savedManualDeliveryOptionId: selection?.savedManualDeliveryOptionId,
      savedManualDeliveryOption: selection?.savedManualDeliveryOption,
      deliveryDetails: {
        selectionType: "manual",
        manualEntry: selection?.deliveryDetails?.manualEntry || {
          cpt: "",
          deliveryCharge: 0,
        },
      },
    });
  }, [
    isManualSelected,
    supplier.supplierId,
    selection,
    onSelectionChange,
    hasSavedManualDeliveryOption,
  ]);

  const handleHold = useCallback(() => {
    onSelectionChange({
      supplierId: supplier.supplierId,
      customerAction: selection?.customerAction === "hold" ? "confirm" : "hold",
      savedManualDeliveryOptionId: selection?.savedManualDeliveryOptionId,
      savedManualDeliveryOption: selection?.savedManualDeliveryOption,
    });
  }, [supplier.supplierId, selection, onSelectionChange]);

  const handleCancel = useCallback(() => {
    onSelectionChange({
      supplierId: supplier.supplierId,
      customerAction:
        selection?.customerAction === "cancel" ? "confirm" : "cancel",
      savedManualDeliveryOptionId: selection?.savedManualDeliveryOptionId,
      savedManualDeliveryOption: selection?.savedManualDeliveryOption,
    });
  }, [supplier.supplierId, selection, onSelectionChange]);

  const handleManualEntryCancel = useCallback(() => {
    if (isExpired) return;
    // Cancel should clear values and unselect manual option.
    onSelectionChange(undefined);
  }, [isExpired, onSelectionChange]);

  const handleManualEntrySave = useCallback(async () => {
    if (isExpired) return;
    if (!isManualSelected) return;
    if (hasSavedManualDeliveryOption) return;
    if (isSavingManualOption) return;

    setManualSaveError(null);

    const entry = selection?.deliveryDetails?.manualEntry;
    if (!entry?.cpt || !entry?.cptSlab) {
      setManualSaveError("Please select CPT and CPT slab before saving.");
      return;
    }

    const deliveryCharge = entry.deliveryCharge;
    if (
      deliveryCharge === undefined ||
      deliveryCharge === null ||
      Number.isNaN(Number(deliveryCharge))
    ) {
      setManualSaveError("Please enter a valid delivery charge before saving.");
      return;
    }

    const minCharge =
      entry.cptSlab === CPTSlabForManualOption.DIRECT_DELIVERY
        ? (directDeliveryMinimumDeliveryCost ?? 50)
        : 0;

    if (
      entry.cptSlab !== CPTSlabForManualOption.SELF_PICKUP &&
      Number(deliveryCharge) < minCharge
    ) {
      setManualSaveError(
        `Delivery charge must be at least ₹${minCharge} for Direct Delivery.`,
      );
      return;
    }

    if (
      entry.cptSlab === CPTSlabForManualOption.SELF_PICKUP &&
      Number(deliveryCharge) !== 0
    ) {
      setManualSaveError("Delivery charge must be ₹0 for Self Pickup.");
      return;
    }

    const cptUtc = dayjs.tz(entry.cpt, "Asia/Kolkata").utc().toISOString();

    try {
      setIsSavingManualOption(true);
      const resp = await services.createManualDeliveryOptionForEstimateSplit(
        estimateNo,
        supplier.splitId,
        {
          cptMin: cptUtc,
          cptMax: cptUtc,
          cptSlab: entry.cptSlab,
          deliveryCost: Number(deliveryCharge),
        },
      );

      onSelectionChange({
        supplierId: supplier.supplierId,
        customerAction: "confirm",
        savedManualDeliveryOptionId: resp.deliveryOption._id,
        savedManualDeliveryOption: {
          cptMin: resp.deliveryOption.cptMin,
          cptMax: resp.deliveryOption.cptMax,
          deliveryCost: resp.deliveryOption.deliveryCost,
          cptSlab: Number(resp.deliveryOption.cptSlab),
        },
        deliveryDetails: {
          selectionType: "manual",
          manualEntry: entry,
        },
      });
    } catch (e: unknown) {
      setManualSaveError(
        e instanceof Error
          ? e.message
          : "Failed to save manual delivery option.",
      );
    } finally {
      setIsSavingManualOption(false);
    }
  }, [
    isExpired,
    isManualSelected,
    hasSavedManualDeliveryOption,
    isSavingManualOption,
    selection,
    directDeliveryMinimumDeliveryCost,
    estimateNo,
    supplier.splitId,
    supplier.supplierId,
    onSelectionChange,
  ]);

  const displayTitle = isRemainingItems
    ? "Supplier Not Found"
    : supplier.supplierName;

  const radioValue = useMemo(() => {
    if (isManualSelected) return "manual";
    if (selectedOptionIndex !== undefined) return String(selectedOptionIndex);
    return "";
  }, [isManualSelected, selectedOptionIndex]);

  const isBuyTimeSelected = selection?.customerAction === "buytime";

  return (
    <Box
      bg="white"
      borderRadius="lg"
      border="1px solid"
      borderColor="orange.300"
      overflow="hidden"
      mb={4}
      boxShadow="sm"
      transition="all 0.2s"
      _hover={{ boxShadow: "md" }}
    >
      {/* Supplier Header */}
      <Box
        bg="gray.50"
        px={4}
        py={3}
        borderBottom="1px solid"
        borderColor="orange.200"
      >
        <Flex justify="space-between" align="center">
          <HStack spacing={3} align="flex-start">
            <VStack align="flex-start" spacing={0}>
              <Text
                fontSize="0.95rem"
                fontWeight="800"
                color="gray.700"
                fontFamily="'Nunito','Inter', sans-serif"
                textTransform="uppercase"
                letterSpacing="wide"
              >
                {displayTitle}
              </Text>
              {!isRemainingItems && supplier.supplierCity && (
                <HStack spacing={1}>
                  <MdLocationOn size="12" color="#3b82f6" />
                  <Text
                    fontSize="0.75rem"
                    color="gray.600"
                    fontFamily="'Nunito','Inter', sans-serif"
                    fontWeight="600"
                  >
                    {supplier.supplierCity}
                  </Text>
                </HStack>
              )}
            </VStack>
            <HStack spacing={2} align="center">
              {!isRemainingItems && supplier.transactionType && (
                <HStack
                  spacing={1}
                  bg="white"
                  px={2}
                  py={0.5}
                  borderRadius="full"
                  border="1px solid"
                  borderColor="gray.200"
                >
                  <Text
                    fontSize="0.7rem"
                    color="gray.500"
                    fontFamily="'Nunito','Inter', sans-serif"
                    fontWeight="700"
                  >
                    {(() => {
                      const tt = String(supplier.transactionType).toUpperCase();
                      if (supplier.transactionType === "Trading") {
                        let text = tt;
                        const disc = Number(supplier.discountPercentage);
                        if (!isNaN(disc) && disc > 0) {
                          text += ` · ${disc}% DISCOUNT`;
                        }
                        const cred = Number(supplier.creditNotePercentage);
                        if (!isNaN(cred) && cred > 0) {
                          text += ` · ${cred}% CREDIT`;
                        }
                        return text;
                      }
                      return tt;
                    })()}
                  </Text>
                </HStack>
              )}
              {!isRemainingItems && supplier.distanceKm !== undefined && (
                <HStack
                  spacing={1}
                  bg="white"
                  px={2}
                  py={0.5}
                  borderRadius="full"
                  border="1px solid"
                  borderColor="gray.200"
                >
                  <MdLocationOn size="12" color="#3b82f6" />
                  <Text
                    fontSize="0.7rem"
                    color="gray.500"
                    fontFamily="'Nunito','Inter', sans-serif"
                    fontWeight="600"
                  >
                    {supplier.distanceKm.toFixed(1)} km
                  </Text>
                </HStack>
              )}
              {!isRemainingItems && supplier.vehicleType !== undefined && (
                <HStack
                  spacing={1}
                  bg="white"
                  px={2}
                  py={0.5}
                  borderRadius="full"
                  border="1px solid"
                  borderColor="gray.200"
                >
                  <Text
                    fontSize="0.7rem"
                    color="gray.500"
                    fontFamily="'Nunito','Inter', sans-serif"
                    fontWeight="700"
                  >
                    {getVehicleTypeDisplay(supplier.vehicleType)?.label || ""}
                  </Text>
                  {getVehicleTypeDisplay(supplier.vehicleType)?.icon && (
                    <Icon
                      as={getVehicleTypeDisplay(supplier.vehicleType)?.icon}
                      color="blue.500"
                      boxSize={3}
                    />
                  )}
                </HStack>
              )}
              <HStack
                spacing={1}
                bg="white"
                px={2}
                py={0.5}
                borderRadius="full"
                border="1px solid"
                borderColor="gray.200"
              >
                <Text
                  fontSize="0.7rem"
                  color="gray.500"
                  fontFamily="'Nunito','Inter', sans-serif"
                  fontWeight="700"
                >
                  MRP: ₹{supplierFulfillingItemsMRP.toLocaleString("en-IN")}
                </Text>
              </HStack>
            </HStack>
          </HStack>

          {/* Action Area */}
          <VStack align="flex-end" spacing={1}>
            {/* Action Buttons: Hold | Cancel split | Buy time - Pill style */}
            <HStack spacing={3} align="center">
              <ActionPillButton
                label="HOLD"
                isSelected={selection?.customerAction === "hold"}
                onClick={handleHold}
                isDisabled={isExpired}
                colorScheme="gray"
              />
              <ActionPillButton
                label="CANCEL"
                isSelected={selection?.customerAction === "cancel"}
                onClick={handleCancel}
                isDisabled={isExpired}
                colorScheme="orange"
              />
              <VStack spacing={0.5} align="center">
                <Box
                  as="button"
                  px={3}
                  py={1}
                  h="32px"
                  borderRadius="full"
                  border="1px solid"
                  borderColor={isBuyTimeSelected ? "blue.500" : "gray.400"}
                  bg={isBuyTimeSelected ? "blue.100" : "white"}
                  cursor={isExpired ? "not-allowed" : "pointer"}
                  opacity={isExpired ? 0.5 : 1}
                  display="flex"
                  alignItems="center"
                  gap={2}
                  transition="all 0.2s ease"
                  _hover={
                    isExpired
                      ? {}
                      : {
                          borderColor: isBuyTimeSelected
                            ? "blue.600"
                            : "blue.300",
                          bg: isBuyTimeSelected ? "blue.100" : "blue.50",
                        }
                  }
                  _active={
                    isExpired
                      ? {}
                      : {
                          borderColor: "blue.600",
                          bg: "blue.100",
                        }
                  }
                  onClick={() => {
                    if (isExpired) return;
                    const newAction = isBuyTimeSelected ? "confirm" : "buytime";

                    onSelectionChange({
                      supplierId: supplier.supplierId,
                      customerAction: newAction,
                      savedManualDeliveryOptionId:
                        selection?.savedManualDeliveryOptionId,
                      savedManualDeliveryOption:
                        selection?.savedManualDeliveryOption,
                      buyTimeHours:
                        newAction === "buytime" ? buyTimeValue : undefined,
                    });
                  }}
                >
                  <Text
                    fontSize="0.8rem"
                    fontWeight={isBuyTimeSelected ? "700" : "600"}
                    color={isBuyTimeSelected ? "blue.700" : "gray.700"}
                    fontFamily="'Nunito','Inter', sans-serif"
                  >
                    BUY TIME
                  </Text>
                  <InputGroup
                    size="xs"
                    w="55px"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Input
                      type="number"
                      min={0.1}
                      step={0.1}
                      placeholder="-"
                      textAlign="center"
                      value={buyTimeValue}
                      onChange={(e) => {
                        const val = e.target.value;
                        setBuyTimeValue(val);

                        if (!val) {
                          onSelectionChange({
                            supplierId: supplier.supplierId,
                            customerAction: "buytime",
                            savedManualDeliveryOptionId:
                              selection?.savedManualDeliveryOptionId,
                            savedManualDeliveryOption:
                              selection?.savedManualDeliveryOption,
                            buyTimeHours: undefined,
                          });
                          return;
                        }

                        const hours = parseFloat(val);
                        if (isNaN(hours) || hours <= 0) {
                          return;
                        }

                        onSelectionChange({
                          supplierId: supplier.supplierId,
                          customerAction: "buytime",
                          savedManualDeliveryOptionId:
                            selection?.savedManualDeliveryOptionId,
                          savedManualDeliveryOption:
                            selection?.savedManualDeliveryOption,
                          buyTimeHours: val,
                        });
                      }}
                      isDisabled={isExpired}
                      borderColor="gray.400"
                      borderRadius="full"
                      fontSize="0.75rem"
                      fontWeight="600"
                      bg="white"
                      _focus={{
                        borderColor: "blue.400",
                        boxShadow: "0 0 0 1px var(--chakra-colors-blue-400)",
                      }}
                    />
                    <InputRightElement width="32px" pointerEvents="none">
                      <Text
                        fontSize="0.65rem"
                        color="gray.500"
                        mr={1}
                        fontWeight="800"
                      >
                        hrs
                      </Text>
                    </InputRightElement>
                  </InputGroup>
                </Box>
                <Box
                  h="0"
                  overflow="visible"
                  display="flex"
                  justifyContent="center"
                  w="full"
                >
                  <Text
                    fontSize="0.6rem"
                    fontWeight="600"
                    color={isBuyTimeSelected ? "blue.500" : "gray.600"}
                    fontFamily="'Nunito','Inter', sans-serif"
                    whiteSpace="nowrap"
                    mt="-2px"
                  >
                    Offline Discovery
                  </Text>
                </Box>
              </VStack>
            </HStack>
          </VStack>
        </Flex>
      </Box>

      {/* Parts and Delivery Selection Grid */}
      <Flex direction={{ base: "column", md: "row" }}>
        {/* Parts List - Scrollable */}
        <Box
          flex="1"
          px={4}
          py={3}
          borderRight={{ base: "none", md: "1px solid" }}
          borderBottom={{ base: "1px solid", md: "none" }}
          borderColor="gray.100"
          bg="gray.50"
        >
          <HStack mb={2} spacing={4}>
            <Text
              fontSize="0.7rem"
              color="gray.500"
              fontFamily="'Nunito','Inter', sans-serif"
              fontWeight="700"
              textTransform="uppercase"
              flex="1"
            >
              Part Details
            </Text>
            <Text
              fontSize="0.7rem"
              color="gray.500"
              fontFamily="'Nunito','Inter', sans-serif"
              fontWeight="700"
              textTransform="uppercase"
              w="40px"
              textAlign="right"
            >
              Qty
            </Text>
          </HStack>

          <Box
            maxH="180px"
            overflowY="auto"
            pr={1}
            css={{
              "&::-webkit-scrollbar": { width: "4px" },
              "&::-webkit-scrollbar-track": { background: "transparent" },
              "&::-webkit-scrollbar-thumb": {
                background: "#CBD5E0",
                borderRadius: "2px",
              },
            }}
          >
            <VStack align="stretch" spacing={2}>
              {supplier.parts.map((part, index) => (
                <HStack
                  key={`${part.partNumber}-${index}`}
                  spacing={4}
                  pb={1}
                  borderBottom="1px dashed"
                  borderColor="gray.200"
                  _last={{ borderBottom: "none" }}
                >
                  <VStack align="start" spacing={0} flex="1">
                    <Text
                      fontSize="0.8rem"
                      color="gray.700"
                      fontFamily="'Nunito','Inter', sans-serif"
                      fontWeight="700"
                      lineHeight="1"
                    >
                      {part.partNumber}
                    </Text>
                    <Text
                      fontSize="0.8rem"
                      color="gray.600"
                      fontFamily="'Nunito','Inter', sans-serif"
                      fontWeight="500"
                      mt="1"
                      lineHeight="1.1"
                      wordBreak="break-all"
                    >
                      {part.partName}
                    </Text>
                  </VStack>
                  <Text
                    fontSize="0.8rem"
                    color="gray.700"
                    fontFamily="'Nunito','Inter', sans-serif"
                    textAlign="right"
                    fontWeight="600"
                  >
                    {part.quantity}
                  </Text>
                </HStack>
              ))}
            </VStack>
          </Box>
        </Box>

        {/* Delivery Selection */}
        <Box flex="1.4" px={5} py={4}>
          <Flex justify="space-between" align="center" mb={4} pl={0}>
            <HStack spacing={1}>
              <Text
                fontSize="0.85rem"
                fontWeight="700"
                color="gray.700"
                fontFamily="'Nunito','Inter', sans-serif"
              >
                Delivery Options
              </Text>
              <Text color="red.500" fontWeight="bold">
                *
              </Text>
            </HStack>
            <Badge
              colorScheme="orange"
              variant="subtle"
              fontSize="0.65rem"
              mr={0}
            >
              SELECT ONE
            </Badge>
          </Flex>

          {/* Options List with Manual Entry */}
          <RadioGroup
            value={radioValue}
            onChange={(value) => {
              if (value === "manual") {
                handleManualOptionSelect();
              } else {
                handleOptionSelect(parseInt(value));
              }
            }}
            isDisabled={isExpired}
          >
            <VStack align="stretch" spacing={2}>
              {/* Table Header */}
              <HStack spacing={4} px={0} mb={1}>
                {/* Vehicle Type column removed as it's now at supplier level */}
                <Text
                  fontSize="0.7rem"
                  color="gray.500"
                  fontWeight="700"
                  textTransform="uppercase"
                  flex="1"
                >
                  CUSTOMER PROMISE TIME
                </Text>
                <Text
                  fontSize="0.7rem"
                  color="gray.500"
                  fontWeight="700"
                  textTransform="uppercase"
                  w="140px"
                  textAlign="right"
                >
                  Delivery Charge
                </Text>
              </HStack>

              {/* API Delivery Options */}
              {supplier.deliveryOptions.map((option, index) => {
                const isSelected = selectedOptionIndex === index;
                const showDeliveryModeBadge =
                  option.deliveryMode &&
                  option.deliveryMode !== DeliveryModes.DOOR_DELIVERY;
                const showCptSlabBadge =
                  option.cptSlab === CPTSlabForManualOption.DIRECT_DELIVERY ||
                  option.cptSlab ===
                    CPTSlabForManualOption.AGGREGATED_DELIVERY ||
                  option.cptSlab === CPTSlabForManualOption.SELF_PICKUP;

                return (
                  <HStack
                    key={`option-${index}`}
                    spacing={4}
                    p={3}
                    borderRadius="md"
                    bg={isSelected ? "blue.50" : "white"}
                    border="1px solid"
                    borderColor={isSelected ? "blue.400" : "gray.200"}
                    cursor={isExpired ? "not-allowed" : "pointer"}
                    opacity={isExpired ? 0.6 : 1}
                    transition="all 0.2s"
                    _hover={{
                      borderColor: isExpired ? "gray.200" : "blue.300",
                      bg: isExpired
                        ? undefined
                        : isSelected
                          ? "blue.50"
                          : "gray.50",
                      transform: isExpired ? "none" : "translateY(-1px)",
                      shadow: isExpired ? "none" : "sm",
                    }}
                    onClick={() => !isExpired && handleOptionSelect(index)}
                    position="relative"
                  >
                    <HStack flex="1" spacing={2}>
                      <Text
                        fontSize="0.85rem"
                        color="gray.700"
                        fontFamily="'Nunito','Inter', sans-serif"
                        fontWeight="600"
                      >
                        {formatCPTRange(option.cptMin, option.cptMax)}
                      </Text>
                      {showDeliveryModeBadge && (
                        <Badge
                          bg="#D8E6FD"
                          color="#1D3AA1"
                          fontSize="0.65rem"
                          fontWeight="700"
                          px={2}
                          py={0.5}
                          borderRadius="md"
                          textTransform="uppercase"
                        >
                          {formatDeliveryModeLabel(option.deliveryMode!)}
                        </Badge>
                      )}
                    </HStack>
                    {/* Delivery Charge + DD/AD badge (top-right) */}
                    <VStack spacing={1} align="flex-end">
                      {showCptSlabBadge && (
                        <Badge
                          bg={
                            option.cptSlab ===
                            CPTSlabForManualOption.DIRECT_DELIVERY
                              ? "orange.100"
                              : "purple.100"
                          }
                          color={
                            option.cptSlab ===
                            CPTSlabForManualOption.DIRECT_DELIVERY
                              ? "orange.700"
                              : "purple.700"
                          }
                          fontSize="0.65rem"
                          fontWeight="900"
                          px={2}
                          py={0.5}
                          borderRadius="md"
                          textTransform="uppercase"
                        >
                          {option.cptSlab ===
                          CPTSlabForManualOption.DIRECT_DELIVERY
                            ? "DD"
                            : option.cptSlab ===
                                CPTSlabForManualOption.AGGREGATED_DELIVERY
                              ? "AD"
                              : "SP"}
                        </Badge>
                      )}
                      {option.deliveryCostDiscountPercentage ? (
                        <HStack spacing={2} align="center">
                          <Box position="relative">
                            <Text
                              fontSize="0.8rem"
                              color="gray.500"
                              fontFamily="'Nunito','Inter', sans-serif"
                              fontWeight="600"
                            >
                              ₹{option.deliveryCost}
                            </Text>
                            {/* Diagonal Line Overlay */}
                            <Box
                              position="absolute"
                              top="50%"
                              left="-10%"
                              right="-10%"
                              height="1.2px"
                              bg="gray.500"
                              transform="rotate(-12deg)"
                              transformOrigin="center"
                              pointerEvents="none"
                            />
                          </Box>
                          <Text
                            fontSize="0.85rem"
                            color="gray.800"
                            fontFamily="'Nunito','Inter', sans-serif"
                            fontWeight="800"
                            lineHeight="1"
                          >
                            ₹
                            {calculateDiscountedPrice(
                              option.deliveryCost,
                              option.deliveryCostDiscountPercentage,
                            )}
                          </Text>
                        </HStack>
                      ) : (
                        <Text
                          fontSize="0.85rem"
                          color="gray.800"
                          fontFamily="'Nunito','Inter', sans-serif"
                          fontWeight="700"
                          lineHeight="1"
                        >
                          ₹ {option.deliveryCost}
                        </Text>
                      )}
                    </VStack>
                  </HStack>
                );
              })}

              {/* Manual Delivery Option form (only before first save) */}
              {!hasSavedManualDeliveryOption && (
                <VStack
                  align="stretch"
                  spacing={2}
                  p={3}
                  borderRadius="md"
                  bg={isManualSelected ? "blue.50" : "white"}
                  border="1px solid"
                  borderColor={isManualSelected ? "blue.400" : "gray.200"}
                  cursor={isExpired ? "not-allowed" : "pointer"}
                  opacity={isExpired ? 0.6 : 1}
                  transition="all 0.2s"
                  _hover={{
                    borderColor: isExpired ? "gray.200" : "blue.300",
                    bg: isExpired
                      ? undefined
                      : isManualSelected
                        ? "blue.50"
                        : "gray.50",
                    transform: isExpired ? "none" : "translateY(-1px)",
                    shadow: isExpired ? "none" : "sm",
                  }}
                  onClick={() => !isExpired && handleManualOptionSelect()}
                >
                  <Flex justify="space-between" align="center" w="100%">
                    <HStack spacing={3} align="center">
                      <Text
                        fontSize="0.75rem"
                        fontWeight="700"
                        textTransform="uppercase"
                        color="purple.600"
                        fontFamily="'Nunito','Inter', sans-serif"
                        letterSpacing="wider"
                      >
                        MANUAL DELIVERY OPTION
                      </Text>
                    </HStack>

                    {isManualSelected && (
                      <CPTSlabSelector
                        value={selection?.deliveryDetails?.manualEntry?.cptSlab}
                        onChange={(val) => {
                          handleManualCPTSlabChange(val);
                        }}
                        isDisabled={isExpired}
                      />
                    )}
                  </Flex>

                  {/* Manual entry input fields - shown when selected */}
                  {isManualSelected && (
                    <VStack
                      align="stretch"
                      spacing={3}
                      mt={2}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <>
                        {/* Quick ETA Tags */}
                        <Box>
                          <Text
                            fontSize="0.7rem"
                            color="gray.500"
                            fontWeight="700"
                            mb={2}
                          >
                            QUICK SELECT CPT
                          </Text>
                          <Flex wrap="wrap" gap={2}>
                            {quickEtaOptions.map((opt) => (
                              <Tag
                                key={opt.label}
                                size="sm"
                                variant="subtle"
                                colorScheme="blue"
                                cursor={isExpired ? "not-allowed" : "pointer"}
                                onClick={() =>
                                  handleQuickEtaSelect(opt.minutes)
                                }
                                opacity={isExpired ? 0.6 : 1}
                                _hover={{
                                  bg: isExpired ? undefined : "blue.100",
                                }}
                                fontFamily="'Nunito','Inter', sans-serif"
                              >
                                {opt.label}
                              </Tag>
                            ))}
                          </Flex>
                        </Box>

                        {/* CPT and Delivery Charge Inputs */}
                        <HStack spacing={4} align="start">
                          <Input
                            type="datetime-local"
                            size="sm"
                            fontSize="0.85rem"
                            fontWeight="500"
                            placeholder="Select CPT"
                            borderColor={
                              selection?.deliveryDetails?.manualEntry?.cpt
                                ? "gray.300"
                                : "red.300"
                            }
                            _focus={{
                              borderColor: "blue.400",
                              boxShadow:
                                "0 0 0 1px var(--chakra-colors-blue-400)",
                            }}
                            value={
                              selection?.deliveryDetails?.manualEntry?.cpt || ""
                            }
                            onChange={(e) =>
                              handleManualCPTChange(e.target.value)
                            }
                            isDisabled={isExpired}
                            flex="1"
                          />
                          <InputGroup w="140px" size="sm">
                            <InputLeftElement
                              pointerEvents="none"
                              fontSize="0.8rem"
                              color="gray.500"
                            >
                              ₹
                            </InputLeftElement>
                            <Input
                              type="text"
                              inputMode="numeric"
                              pattern="[0-9]*"
                              placeholder="0"
                              fontSize="0.85rem"
                              fontWeight="600"
                              textAlign="right"
                              value={
                                selection?.deliveryDetails?.manualEntry
                                  ?.deliveryCharge ?? ""
                              }
                              onChange={handleManualChargeChange}
                              isDisabled={
                                isExpired ||
                                selection?.deliveryDetails?.manualEntry
                                  ?.cptSlab ===
                                  CPTSlabForManualOption.SELF_PICKUP
                              }
                              borderColor={
                                selection?.deliveryDetails?.manualEntry
                                  ?.deliveryCharge !== undefined
                                  ? "gray.300"
                                  : "red.300"
                              }
                              _focus={{
                                borderColor: "blue.400",
                                boxShadow:
                                  "0 0 0 1px var(--chakra-colors-blue-400)",
                              }}
                            />
                          </InputGroup>
                        </HStack>
                        {errorsByField.deliveryCharge?.map(
                          (error: ValidationError) => (
                            <Text
                              key={error.id}
                              fontSize="12px"
                              fontWeight="semibold"
                              color="red.500"
                              mt={1}
                              ml={1}
                            >
                              *{error.message}
                            </Text>
                          ),
                        )}

                        <HStack justify="flex-end" spacing={2} pt={1}>
                          <Button
                            size="sm"
                            variant="outline"
                            borderRadius="full"
                            onClick={handleManualEntryCancel}
                            isDisabled={isExpired}
                            fontFamily="'Nunito','Inter', sans-serif"
                            fontWeight="700"
                          >
                            CANCEL
                          </Button>
                          <Button
                            size="sm"
                            colorScheme="blue"
                            borderRadius="full"
                            onClick={handleManualEntrySave}
                            isLoading={isSavingManualOption}
                            isDisabled={
                              isExpired ||
                              !selection?.deliveryDetails?.manualEntry?.cpt ||
                              !selection?.deliveryDetails?.manualEntry?.cptSlab
                            }
                            fontFamily="'Nunito','Inter', sans-serif"
                            fontWeight="800"
                          >
                            SAVE
                          </Button>
                        </HStack>
                        {manualSaveError && (
                          <Text
                            fontSize="12px"
                            fontWeight="semibold"
                            color="red.500"
                            mt={1}
                            ml={1}
                          >
                            *{manualSaveError}
                          </Text>
                        )}
                      </>
                    </VStack>
                  )}
                </VStack>
              )}

              {/* Saved manual option row (clickable, option-style) */}
              {hasSavedManualDeliveryOption && savedManualOption && (
                <HStack
                  spacing={4}
                  p={3}
                  borderRadius="md"
                  bg={isManualSelected ? "blue.50" : "white"}
                  border="1px solid"
                  borderColor={isManualSelected ? "blue.400" : "gray.200"}
                  cursor={isExpired ? "not-allowed" : "pointer"}
                  opacity={isExpired ? 0.6 : 1}
                  transition="all 0.2s"
                  position="relative"
                  _hover={{
                    borderColor: isExpired ? "gray.200" : "blue.300",
                    bg: isExpired
                      ? undefined
                      : isManualSelected
                        ? "blue.50"
                        : "gray.50",
                    transform: isExpired ? "none" : "translateY(-1px)",
                    shadow: isExpired ? "none" : "sm",
                  }}
                  onClick={() => !isExpired && handleManualOptionSelect()}
                >
                  <HStack flex="1" spacing={2}>
                    <Text
                      fontSize="0.85rem"
                      color="gray.700"
                      fontFamily="'Nunito','Inter', sans-serif"
                      fontWeight="600"
                    >
                      {formatCPTRange(
                        savedManualOption.cptMin,
                        savedManualOption.cptMax,
                      )}
                    </Text>
                    <Badge
                      bg="purple.100"
                      color="purple.700"
                      fontSize="0.65rem"
                      fontWeight="800"
                      px={2}
                      py={0.5}
                      borderRadius="md"
                      textTransform="uppercase"
                    >
                      MANUAL
                    </Badge>
                    {savedManualOption.cptSlab !== undefined && (
                      <Badge
                        bg="orange.100"
                        color="orange.700"
                        fontSize="0.65rem"
                        fontWeight="900"
                        px={2}
                        py={0.5}
                        borderRadius="md"
                        textTransform="uppercase"
                      >
                        {savedManualOption.cptSlab ===
                        CPTSlabForManualOption.DIRECT_DELIVERY
                          ? "DD"
                          : savedManualOption.cptSlab ===
                              CPTSlabForManualOption.AGGREGATED_DELIVERY
                            ? "AD"
                            : "SP"}
                      </Badge>
                    )}
                  </HStack>
                  <VStack spacing={1} align="flex-end">
                    <Text
                      fontSize="0.85rem"
                      color="gray.800"
                      fontFamily="'Nunito','Inter', sans-serif"
                      fontWeight="700"
                      lineHeight="1"
                    >
                      ₹ {savedManualOption.deliveryCost}
                    </Text>
                  </VStack>
                </HStack>
              )}
            </VStack>
          </RadioGroup>
        </Box>
      </Flex>
    </Box>
  );
};

export default EstimateSplitCard;
